import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Block, BlogPost, FormElement } from "../types";
import type { ColorScheme } from "../types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

interface BlockRendererProps {
  block: Block;
  sectionTheme: string;
  colors: ColorScheme;
  onShowForm?: () => void;
  blogPosts?: BlogPost[];
  formElements?: FormElement[];
  onFormSubmit?: (formData: Record<string, string>) => void;
  isSubmitting?: boolean;
  formTitle?: string;
}

export const BlockRenderer = ({ 
  block, 
  sectionTheme, 
  colors, 
  onShowForm, 
  blogPosts = [],
  formElements = [],
  onFormSubmit,
  isSubmitting,
  formTitle
}: BlockRendererProps) => {
  const navigate = useNavigate();
  const { subdomain, slug } = useParams();
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (block.type === "countdown" && block.content) {
      const targetDate = new Date(block.content).getTime();
      const updateTimer = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;
        setTimeLeft(Math.max(0, Math.floor(distance / 1000)));
      };
      
      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [block]);

  const getButtonStyles = (blockTheme: Block["theme"] = "light") => {
    switch (blockTheme) {
      case "primary":
        return {
          background: colors.primary,
          color: colors.light,
        };
      case "dark":
        return {
          background: colors.dark,
          color: colors.light,
        };
      case "light":
      default:
        return {
          background: colors.light,
          color: colors.dark,
        };
    }
  };

  const getTextColor = () => {
    return sectionTheme === "dark" ? colors.light : 
           sectionTheme === "primary" ? colors.light :
           colors.dark;
  };

  const formatTime = (seconds: number) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const remainingSeconds = seconds % 60;
    
    return `${days}d ${hours}h ${minutes}m ${remainingSeconds}s`;
  };

  const handlePostClick = (postId: string) => {
    const currentPageSlug = slug || '';
    navigate(`/f/${subdomain}/${currentPageSlug}/${postId}`);
  };

  const renderEmbedContent = () => {
    if (!block.embedCode) return null;
    
    switch (block.embedType) {
      case "iframe":
        return <div dangerouslySetInnerHTML={{ __html: block.embedCode }} />;
      case "script":
        return (
          <script
            dangerouslySetInnerHTML={{ __html: block.embedCode }}
          />
        );
      case "html":
        return <div dangerouslySetInnerHTML={{ __html: block.embedCode }} />;
      default:
        return null;
    }
  };

  const renderTextBlockStyles = () => {
    const textColor = getTextColor();
    return (
      <style>
        {`
          .block-${block.id} h1,
          .block-${block.id} h2,
          .block-${block.id} h3,
          .block-${block.id} h4,
          .block-${block.id} h5,
          .block-${block.id} h6,
          .block-${block.id} p,
          .block-${block.id} blockquote,
          .block-${block.id} ul li,
          .block-${block.id} ol li {
            color: ${textColor} !important;
          }
        `}
      </style>
    );
  };

  switch (block.type) {
    case "feature-grid":
      const features = JSON.parse(block.content || '{"features":[]}').features;
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature: any, index: number) => (
            <div key={index} className="text-center p-6 rounded-lg bg-white/50 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.primary }}>
                {feature.title}
              </h3>
              <p className="text-sm" style={{ color: getTextColor() }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      );

    case "video":
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden">
          <iframe
            src={block.content}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );

    case "countdown":
      return (
        <div className="text-center p-6 rounded-lg bg-white/50 backdrop-blur-sm">
          <div className="text-2xl font-bold mb-2" style={{ color: colors.primary }}>
            {formatTime(timeLeft)}
          </div>
          {block.content && (
            <p className="text-sm" style={{ color: getTextColor() }}>
              Offer ends in:
            </p>
          )}
        </div>
      );

    case "faq":
      const faqs = JSON.parse(block.content || '{"faqs":[]}').faqs;
      return (
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq: any, index: number) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left" style={{ color: colors.primary }}>
                {faq.question}
              </AccordionTrigger>
              <AccordionContent style={{ color: getTextColor() }}>
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );

    case "progress":
      const progress = JSON.parse(block.content || '{"progress":0, "label":""}');
      return (
        <div className="space-y-2">
          <div className="flex justify-between text-sm" style={{ color: getTextColor() }}>
            <span>{progress.label}</span>
            <span>{progress.progress}%</span>
          </div>
          <Progress value={progress.progress} className="w-full" />
        </div>
      );

    case "posts":
      return (
        <div className="grid gap-6">
          <h3 className="text-xl font-semibold">{block.postsConfig?.title || 'Latest Posts'}</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.slice(0, block.postsConfig?.postsPerPage || 6).map((post) => (
              <div 
                key={post.id} 
                className="border rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handlePostClick(post.slug || post.id)}
              >
                {post.featuredImage && (
                  <img 
                    src={post.featuredImage} 
                    alt={post.title} 
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <h4 className="font-semibold mb-2">{post.title}</h4>
                <p className="text-sm text-gray-600 mb-2">{post.excerpt}</p>
                <span className="text-xs text-gray-500">
                  {new Date(post.publishedAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    case "text":
      return (
        <>
          {renderTextBlockStyles()}
          <div 
            className={`prose max-w-none block-${block.id}`}
            dangerouslySetInnerHTML={{ __html: block.content }}
          />
        </>
      );
    case "image":
      return block.url ? (
        <a 
          href={block.url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="block mb-8 transform hover:scale-[1.02] transition-all duration-300"
        >
          <img 
            src={block.content} 
            alt="" 
            className="w-full h-auto rounded-xl shadow-lg" 
          />
        </a>
      ) : (
        <img 
          src={block.content} 
          alt="" 
          className="w-full h-auto rounded-xl shadow-lg mb-8" 
        />
      );
    case "button":
      if (block.buttonVariant === "form") {
        return onShowForm ? (
          <Button
            onClick={onShowForm}
            className="mb-6 transform hover:scale-105 transition-all duration-300 shadow-lg"
            style={getButtonStyles(block.theme)}
          >
            {block.content}
          </Button>
        ) : null;
      } else if (block.buttonVariant === "link") {
        return (
          <Button
            asChild
            variant="link"
            className="mb-6 p-0 h-auto font-normal hover:opacity-80 transition-opacity"
            style={getButtonStyles(block.theme)}
          >
            <a
              href={block.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {block.content}
            </a>
          </Button>
        );
      } else {
        return (
          <Button
            asChild
            className="mb-6 transform hover:scale-105 transition-all duration-300 shadow-lg"
            style={getButtonStyles(block.theme)}
          >
            <a
              href={block.url}
              target={block.target === "new" ? "_blank" : "_self"}
              rel={block.target === "new" ? "noopener noreferrer" : undefined}
            >
              {block.content}
            </a>
          </Button>
        );
      }
    case "form":
      return (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (onFormSubmit) {
            const formData: Record<string, string> = {};
            formElements.forEach(element => {
              const input = e.currentTarget.elements.namedItem(element.id) as HTMLInputElement;
              formData[element.id] = input.value;
            });
            onFormSubmit(formData);
          }
        }} className="space-y-6">
          {block.content && (
            <h3 className="text-xl font-semibold mb-4">{block.content}</h3>
          )}
          {formElements.map((element) => {
            switch (element.type) {
              case "text":
              case "email":
                return (
                  <div key={element.id} className="space-y-2">
                    <Label htmlFor={element.id}>
                      {element.title}
                      {element.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <Input
                      id={element.id}
                      name={element.id}
                      type={element.type}
                      required={element.required}
                    />
                  </div>
                );
              case "switch":
                return (
                  <div key={element.id} className="flex items-center space-x-2">
                    <Switch
                      id={element.id}
                      name={element.id}
                    />
                    <Label htmlFor={element.id}>
                      {element.title}
                      {element.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                  </div>
                );
              default:
                return null;
            }
          })}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full"
            style={{ backgroundColor: colors.primary }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </form>
      );
    case "embed":
      return renderEmbedContent();
    default:
      return null;
  }
};
